import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SolidLockAltIcon from 'LuxKit/Icons/solid/SolidLockAltIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import Image from 'components/Common/Image'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff } from 'selectors/accountSelectors'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import {
  MASTERCARD_LOGO_ID,
  LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS,
} from './loyaltyCreditCardConstants'
import { LoyaltyCreditCardCreamSection, loyaltyCreditCardCardShadow } from './components/loyaltyCreditCardStyles'
import LoyaltyCreditCardApplyNowButton from './components/LoyaltyCreditCardApplyNowButton'
import { LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'

const ApplySplitSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(40)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    gap: ${rem(40)};
    align-items: center;
  }
`

const ApplyLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(24)};
  text-align: center;

  ${mediaQueryUp.tablet} {
    flex: 1;
  }
`

const ApplyRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    width: ${rem(420)};
    flex-shrink: 0;
  }
`

const ApplyCardImage = styled.div`
  display: flex;
  justify-content: center;
  background: transparent;

  picture {
    display: block;
    border-radius: ${rem(8)};
    overflow: hidden;
  }

  img {
    max-width: ${rem(200)};
    width: 100%;
    border-radius: ${rem(8)};
    display: block;

    ${mediaQueryUp.tablet} {
      max-width: ${rem(280)};
    }
  }
`

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
  text-align: start;
`

const StepItem = styled.div`
  display: flex;
  gap: ${rem(16)};
  align-items: center;
`

const StepNumber = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 50%;
  background: white;
  ${loyaltyCreditCardCardShadow}
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 600;
  font-size: ${rem(16)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const ApplyCtaWrapper = styled.div`
  display: flex;
  justify-content: center;
`

const DesktopApplyCtaWrapper = styled(ApplyCtaWrapper)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const MobileApplyCtaWrapper = styled(ApplyCtaWrapper)`
  ${mediaQueryDown.mobile} {
    width: 100%;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const ApplyButton = styled(LoyaltyCreditCardApplyNowButton)`
  && {
    min-width: ${rem(148)};
  }

  ${mediaQueryDown.mobile} {
    && {
      width: 100%;
    }
  }
`

const CardTable = styled.div`
  background: white;
  border-radius: ${rem(8)};
  overflow: hidden;
  box-shadow: 0 0 1.5px rgba(0,0,0,0.07), 0 0 2.5px rgba(0,0,0,0.05), 0 0 5px rgba(0,0,0,0.05), 0 0 16px rgba(0,0,0,0.04);
`

const tableRowStyle = css`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding: 0 ${rem(8)};

  &:last-child {
    border-bottom: none;
  }
`

const TableRow = styled.div`
  ${tableRowStyle}
`

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  width: ${rem(128)};
  flex-shrink: 0;
  padding: ${rem(12)} ${rem(8)};
  font-size: ${rem(12)};
  font-weight: 600;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  line-height: 1.4;
`

const TableHeaderDesktopLabel = styled.span`
  display: none;

  ${mediaQueryUp.tablet} {
    display: inline;
  }
`

const TableHeaderMobileLabel = styled.span`
  display: inline;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const TableCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${rem(12)} ${rem(8)};
  font-size: ${rem(12)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  line-height: 1.4;
`

const TableCellParagraph = styled.p`
  margin: 0;

  & + & {
    margin-top: ${rem(8)};
  }
`

const TableCellDivider = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin: ${rem(8)} 0;
`

const TableCellList = styled.ul`
  margin: 0;
  padding-inline-start: ${rem(16)};

  li + li {
    margin-top: ${rem(4)};
  }
`

const SecurityBanner = styled.div`
  padding: ${rem(12)} ${rem(8)};
  background: ${props => props.theme.palette.messaging.success.lightBackground};
  display: flex;
  gap: ${rem(8)};
  align-items: flex-start;

  span {
    font-size: ${rem(12)};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

const TrustPill = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: ${rem(8)};
  box-sizing: border-box;
  width: 100%;
  padding: ${rem(12)} ${rem(20)};

  ${mediaQueryUp.tablet} {
    display: inline-flex;
    align-items: center;
    gap: ${rem(32)};
    width: auto;
    background: white;
    border-radius: ${rem(100)};
    box-shadow: 0 0 0.75px rgba(0,0,0,0.07), 0 0 2.5px rgba(0,0,0,0.05), 0 0 5px rgba(0,0,0,0.05), 0 0 16px rgba(0,0,0,0.04);
  }
`

const TrustItem = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  min-width: 0;
  text-align: center;

  ${mediaQueryUp.tablet} {
    flex: initial;
    flex-direction: row;
    text-align: start;
  }
`

const MastercardLogo = styled.span`
  display: block;
  width: ${rem(45)};
  height: ${rem(28)};

  picture,
  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`

const MoneyMeLogo = styled.span`
  display: block;
  width: ${rem(93)};
  height: ${rem(24)};

  ${mediaQueryUp.tablet} {
    width: ${rem(67)};
    height: ${rem(16)};
  }

  picture,
  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`

const ApplyTrustAndLegal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(24)};
  margin-top: ${rem(32)};
`

const LegalText = styled.div`
  width: 100%;
`

const MONEYME_LOGO_ASPECT_RATIO = '25:6'

function LoyaltyCreditCardApplySection() {
  const intl = useIntl()
  // Employees are on their own offer: no limited time offer row, and a
  // different annual fee.
  const isStaff = useAppSelector(isLEStaff)

  return (
    <LoyaltyCreditCardCreamSection background="light-background" id="how-to-apply">
      <LayoutContainer className={LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} variant="medium">
        <ApplySplitSection>
          <ApplyLeft>
            <ApplyCardImage>
              <Image
                id="societe-apply-le-card"
                fit="center"
                objectFit="cover"
              />
            </ApplyCardImage>
            <Heading variant="heading1" align="center">
              {intl.formatMessage({
                id: 'i36019k1v_bmLXUEXe8u',
                defaultMessage: 'Start earning with Société today.',
                description: 'How to apply section heading on Société Credit Card page',
              })}
            </Heading>
            <StepList>
              <StepItem>
                <StepNumber>{1}</StepNumber>
                <BodyText variant="large">
                  {intl.formatMessage({
                    id: 'OItbB8k20IdEIgD-JtoP',
                    defaultMessage: 'Complete our simple online application',
                    description: 'First step copy in the how to apply section on the Société Credit Card page',
                  })}
                </BodyText>
              </StepItem>
              <StepItem>
                <StepNumber>{2}</StepNumber>
                <BodyText variant="large">
                  {intl.formatMessage({
                    id: 'U3zrDk2MzwaudQ3bl5Yz',
                    defaultMessage: 'Get a decision in as little as 60 minutes',
                    description: 'Second step copy in the how to apply section on the Société Credit Card page',
                  })}
                </BodyText>
              </StepItem>
              <StepItem>
                <StepNumber>{3}</StepNumber>
                <BodyText variant="large">
                  {intl.formatMessage({
                    id: '7jKpszxJSuGfJ6bF988N',
                    defaultMessage: 'Add your card to Apple Pay or Google Pay and start spending',
                    description: 'Third step copy in the how to apply section on the Société Credit Card page',
                  })}
                </BodyText>
              </StepItem>
            </StepList>
            <DesktopApplyCtaWrapper>
              <ApplyButton fit="mobile-full-width" placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.howToApply} />
            </DesktopApplyCtaWrapper>
          </ApplyLeft>

          <ApplyRight>
            <CardTable>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'lCED7hLm4IYczkrRnRWP', defaultMessage: 'Base earn rate', description: 'Label for the base earn rate row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {intl.formatMessage({ id: 'reXLWVVvfSyfmDkDyrow', defaultMessage: 'Earn 2 Société Points per $1 spent on eligible purchases, up to $15,000 per statement period. Earn 1 point per $1 on all other eligible purchases.', description: 'Value for the base earn rate row in the Société Credit Card details table' })}
                </TableCell>
              </TableRow>
              {!isStaff && (
                <TableRow>
                  <TableHeader>
                    <TableHeaderDesktopLabel>
                      {intl.formatMessage({ id: 'Bld4amLxPPHqZ8wy9Rl3', defaultMessage: 'Limited Time Offer', description: 'Label for the limited time offer row in the Société Credit Card details table' })}
                    </TableHeaderDesktopLabel>
                    <TableHeaderMobileLabel>
                      {intl.formatMessage({ id: 'ZpboGJKmKHZYIrrpfQmv', defaultMessage: 'Sign up bonus', description: 'Mobile label for the limited time offer row in the Société Credit Card details table' })}
                    </TableHeaderMobileLabel>
                  </TableHeader>
                  <TableCell>
                    <TableCellParagraph>
                      {intl.formatMessage({
                        id: 'qpMs0QU3cACUadjtnx43',
                        defaultMessage: '1x $150 Luxury Escapes credit when you spend $5,000 in your first 90 days.',
                        description: 'First paragraph in the limited time offer row of the Société Credit Card details table, describing the welcome credit',
                      })}
                    </TableCellParagraph>
                    <TableCellDivider />
                    <TableCellParagraph>
                      {intl.formatMessage({
                        id: 'a0bfQ9lfPVTDwhd8bzDs',
                        defaultMessage: 'Chance to win a Maldives escape – all approved cardholders entered.',
                        description: 'Second paragraph in the limited time offer row of the Société Credit Card details table, describing the Maldives prize draw',
                      })}
                    </TableCellParagraph>
                    <TableCellParagraph>
                      {intl.formatMessage({
                        id: 'QDcolPGmP2Tzw3hVcLPu',
                        defaultMessage: 'First 100 approved cardholders also receive 25,000 bonus Société Points. Offer ends 29 October 2026.',
                        description: 'Third paragraph in the limited time offer row of the Société Credit Card details table, describing the bonus points offer',
                      })}
                    </TableCellParagraph>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'UDdBJKkdebPyMee1ltPY', defaultMessage: 'Credit limit range', description: 'Label for the credit limit range row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {intl.formatMessage({ id: 'AOzB4o4kJe6b3AI1qdcy', defaultMessage: '$2,000 - $30,000', description: 'Value for the credit limit range row in the Société Credit Card details table' })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'GvpuTdS6pRDnRLZIwIO5', defaultMessage: 'Purchase APR', description: 'Label for the purchase APR row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {intl.formatMessage({
                    id: 'J7hh4Wv04XHvl_YnAM20',
                    defaultMessage: 'From 18.99% – 23.99% p.a. Personalised interest rate applies (based on your credit history) after interest-free period.',
                    description: 'Value for the purchase APR row in the Société Credit Card details table',
                  })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'nuPyD0mzaNFitgiTLR-S', defaultMessage: 'Cash advance APR', description: 'Label for the cash advance APR row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {intl.formatMessage({ id: 'L-Fi9H3BiHbNVyB6LiW3', defaultMessage: 'From 21.99% p.a.', description: 'Value for the cash advance APR row in the Société Credit Card details table' })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'jg6LbrWJomZiQVbENV4G', defaultMessage: 'Interest-free period', description: 'Label for the interest-free period row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {intl.formatMessage({ id: 'xd37EE9QCPxAEoygB1Os', defaultMessage: 'Up to 44 days on purchases', description: 'Value for the interest-free period row in the Société Credit Card details table' })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: '6tDlHdOxxEC1qugxnJDY', defaultMessage: 'International transaction fees', description: 'Label for the international fees row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {intl.formatMessage({ id: 'V5Aq2z7HpWv0MTI99LKF', defaultMessage: '0%', description: 'Value for the international fees row in the Société Credit Card details table' })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'mCtBJYXkw6E3gd1X7SBV', defaultMessage: 'Travel benefits', description: 'Label for the travel benefits row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  <TableCellList>
                    <li>
                      {intl.formatMessage({
                        id: 'dYt8kMIuAuyaBdlS0GIx',
                        defaultMessage: '$3,000 travel inconvenience insurance⁶',
                        description: 'First bullet point in the travel benefits row of the Société Credit Card details table',
                      })}
                    </li>
                    <li>
                      {intl.formatMessage({
                        id: 'yYGHvgMs7CESmR3S7WoY',
                        defaultMessage: 'Lounge access when delayed⁷',
                        description: 'Second bullet point in the travel benefits row of the Société Credit Card details table',
                      })}
                    </li>
                    <li>
                      {intl.formatMessage({
                        id: 'FZRsaezy6GwbCz_oGHOi',
                        defaultMessage: 'Mobile phone protection insurance⁵',
                        description: 'Third bullet point in the travel benefits row of the Société Credit Card details table',
                      })}
                    </li>
                  </TableCellList>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'XfNsO_fpriNhmBCG9mkz', defaultMessage: 'Card type', description: 'Label for the card type row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {intl.formatMessage({ id: 'Qs52_oKXBv1sz9f_Q_o1', defaultMessage: 'Digital (Phone wallet). Apple Pay & Google Pay.', description: 'Value for the card type row in the Société Credit Card details table' })}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHeader>
                  {intl.formatMessage({ id: 'FA63EFoSDjXsGlThjQRu', defaultMessage: 'Annual Fee', description: 'Label for the annual fee row in the Société Credit Card details table' })}
                </TableHeader>
                <TableCell>
                  {!isStaff && intl.formatMessage({ id: '19jrknEjxuKaq3oem46H', defaultMessage: '$149/year', description: 'Value for the annual fee row in the Société Credit Card details table' })}
                  {isStaff && intl.formatMessage({
                    id: 'ECSASRkZWKqzDRmGuGMd',
                    defaultMessage: 'LE employee offer: $0 for 2 years, then $149/year',
                    description: 'Value for the annual fee row in the Société Credit Card details table shown to Luxury Escapes employees, whose offer waives the fee for the first two years',
                  })}
                </TableCell>
              </TableRow>
              <SecurityBanner>
                <SolidLockAltIcon size={16} colour="normal-contrast" aria-hidden />
                <span>
                  {intl.formatMessage({
                    id: 'kIoLu3PvL7FgMDblWOXt',
                    defaultMessage: "Secured with Face ID, Touch ID, and Mastercard's global fraud protection.",
                    description: 'Security banner on card details table on Société Credit Card page',
                  })}
                </span>
              </SecurityBanner>
            </CardTable>
            <MobileApplyCtaWrapper>
              <ApplyButton fit="mobile-full-width" placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.howToApply} />
            </MobileApplyCtaWrapper>
          </ApplyRight>
        </ApplySplitSection>
        <ApplyTrustAndLegal>
          <TrustPill>
            <TrustItem>
              <MastercardLogo>
                <Image
                  id={MASTERCARD_LOGO_ID}
                  alt="Mastercard"
                  width={45}
                  height={28}
                  dpr={2}
                  enhancedFormats={false}
                />
              </MastercardLogo>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: '0eCtHs2UtCqXjJA30N6a',
                  defaultMessage: 'Backed by Mastercard',
                  description: 'Trust pill – backed by Mastercard on Société Credit Card page',
                })}
              </BodyText>
            </TrustItem>
            <TrustItem>
              <MoneyMeLogo>
                <Image
                  id="societe-credit-card-moneyme"
                  alt="MoneyMe"
                  aspectRatio={MONEYME_LOGO_ASPECT_RATIO}
                  format="png"
                  width={93}
                  height={24}
                  gravity="center"
                  dpr={2}
                  quality="best"
                />
              </MoneyMeLogo>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'jjGFXriqRMPPVIqI_5-I',
                  defaultMessage: 'Powered by MONEYME',
                  description: 'Trust pill – powered by MoneyMe on Société Credit Card page',
                })}
              </BodyText>
            </TrustItem>
          </TrustPill>
          <LegalText>
            <BodyText variant="small">
              {intl.formatMessage({
                id: '5nxiNLnpK_2ONIASBsIf',
                defaultMessage: 'Société Credit Card issued by MONEYME Financial Group Pty Ltd ABN 40 163 691 236, Australian Credit Licence 442218, and promoted by Luxury Escapes. Australian residents 18+. Approval subject to credit criteria. Fees and charges apply. Consider the Target Market Determination, Key Facts Sheet and full terms at https://www.moneyme.com.au/credit-card/luxury-escapes-kfs before applying. Société Points and voucher subject to Luxury Escapes terms at luxuryescapes.com/au/terms-and-conditions.',
                description: 'Legal disclaimer covering the card issuer, key facts sheet and Luxury Escapes terms on the Société Credit Card page',
              })}
            </BodyText>
            <br />
            <BodyText variant="small">
              {intl.formatMessage({
                id: 'nnAberR4Vcz1Ke3AYB3A',
                defaultMessage: '¹Earn 2 Société Points for each AUD $1 spent up to AUD $15,000 on Eligible Domestic Purchases per month, and 1 Société Point per AUD $1 spent above AUD $15,000 per month. Exclusions apply.',
                description: 'Footnote one legal copy covering domestic points earn rates and exclusions on the Société Credit Card page',
              })}
            </BodyText>
            <br />
            <BodyText variant="small">
              {intl.formatMessage({
                id: 'r_BpyXf-To51KzKF1siZ',
                defaultMessage: '²AUD $150 voucher requires AUD $5,000 spend within 90 days. Store credit only.',
                description: 'Footnote two legal copy covering the $150 voucher spend requirements on the Société Credit Card page',
              })}
            </BodyText>
            <br />
            <BodyText variant="small">
              {intl.formatMessage({
                id: 'yosWC9WSf1sGIFRl4OSS',
                defaultMessage: '³Entry valid for AU residents 18+ approved for a Société Credit Card (issued by MONEYME Financial Group Pty Ltd, Australian Credit Licence 442218, eligibility criteria and fees apply) between 7 September and 29 October 2026. Prize includes 5 nights for 2 at Outrigger Maldives Maafushivaru Resort, flights not included, valued up to A$11,434. Permits ACT TP26/01768, SA T26/1342, NSW TP/05267. For full T&Cs see: https://luxuryescapes.com/au/promotions',
                description: 'Footnote three legal copy covering the Maldives prize draw entry conditions on the Société Credit Card page',
              })}
            </BodyText>
            <br />
            <BodyText variant="small">
              {intl.formatMessage({
                id: 'XmbxH8qvSHjd4TB-BOci',
                defaultMessage: '⁴First 100 eligible cardholders who link their Société account by 29 October 2026 also receive 25,000 Société Points (credited within 60 days, program T&Cs and expiry apply).',
                description: 'Footnote four legal copy covering eligibility for the 25,000 bonus points offer on the Société Credit Card page',
              })}
            </BodyText>
            <br />
            <BodyText variant="small">
              {intl.formatMessage({
                id: 'KtehkP8sHWTd30NQEDLu',
                defaultMessage: '⁵Mobile phone protection insurance is underwritten and issued by AIG Australia Limited ABN 93 004 727 753, AFSL 381 686 (AIG) under a group policy of insurance issued to Mastercard Asia/Pacific Pte. Ltd. In arranging the insurance, Mastercard acts as a group purchasing body under ASIC Corporations (Group Purchasing Bodies) Instrument 2018/751. Eligible persons are current holders of a MONEYME Luxury Escapes Société Mastercard. Cover applies to accidental damage or theft only where the mobile provider’s bill for the billing cycle before the incident (or a prepaid top-up in that period) was paid using the eligible card. Benefits are limited to $900 per claim and $1,500 per twelve month period, with a $37.50 excess. Lost phones, cosmetic damage and accessories are not covered. You should read the Mastercard Plus Products Insurance Terms and Conditions for mobile phone protection. Eligible persons can claim as third party beneficiaries by virtue of the operation of s48 of the Insurance Contracts Act 1984. Conditions, exclusions and limits apply as set out in the Terms and Conditions, which may be amended from time to time. MONEYME Financial Group Pty Ltd does not guarantee this insurance. It does not hold an Australian Financial Services Licence and is not authorised to provide financial advice in relation to the insurance. You should consider obtaining your own advice as to the suitability of the insurance coverage for you.',
                description: 'Footnote five legal copy covering mobile phone protection insurance and exclusions on the Société Credit Card page',
              })}
            </BodyText>
            <br />
            <BodyText variant="small">
              {intl.formatMessage({
                id: '5ViMPzAwGHWE52yyfhfS',
                defaultMessage: '⁶Travel inconvenience insurance is underwritten and issued by AIG Australia Limited ABN 93 004 727 753, AFSL 381 686 (AIG) under a group policy of insurance issued to Mastercard Asia/Pacific Pte. Ltd. In arranging the insurance, Mastercard acts as a group purchasing body under ASIC Corporations (Group Purchasing Bodies) Instrument 2018/751. Eligible persons are holders of a MONEYME Luxury Escapes Société Mastercard aged between 18 and 75 who are ordinarily resident in Australia and whose card is valid, open and in good standing at the time the trip is purchased. Cover applies only where the entire cost of the common carrier tickets was charged to the eligible card, or acquired with points from a rewards program associated with the card, before the date of departure. Return trips only, up to 180 days, and domestic trips must be more than 100 kilometres from your city of permanent residence. Maximum benefits per insured person per covered trip are $1,500 for trip cancellation, $1,500 for trip curtailment, $1,500 for trip postponement, $150 for trip delay (delays over four hours) and $150 for missed connection. Cover extends to your spouse and children. Pre-existing medical conditions are excluded. You should read the Travel Inconvenience Insurance Terms and Conditions. Eligible persons can claim as third party beneficiaries by virtue of the operation of s48 of the Insurance Contracts Act 1984. Conditions, exclusions and limits apply as set out in the Terms and Conditions, which may be amended from time to time. MONEYME Financial Group Pty Ltd does not guarantee this insurance. It does not hold an Australian Financial Services Licence and is not authorised to provide financial advice in relation to the insurance. You should consider obtaining your own advice as to the suitability of the insurance coverage for you.',
                description: 'Footnote six legal copy covering travel inconvenience insurance and exclusions on the Société Credit Card page',
              })}
            </BodyText>
            <br />
            <BodyText variant="small">
              {intl.formatMessage({
                id: 'WGMWkPEUwag8qUrGnee6',
                defaultMessage: '⁷Mastercard Flight Delay Pass is a service, not insurance. It is provided on behalf of Mastercard by Collinson Insurance Services Limited and its affiliates, and is available to holders of a MONEYME Luxury Escapes Société Mastercard aged 18 or over. You must register each flight on the Flight Delay Pass website before the actual departure time. Registrations cannot be amended after submission. You can register yourself and up to one travelling companion on the same flight. Only selected scheduled commercial flights departing from an airport with a LoungeKey lounge are eligible. Charter flights are not eligible. Lounge access is provided, subject to lounge availability, where the flight tracking system records a delay of 120 minutes or more. The lounge pass is sent to your registered email address and is valid for one entry within 24 hours of issue at a lounge in the departure airport. Cancelled flights and flights rescheduled to the next day are not treated as delays. Passes are non-transferable, non-refundable and non-changeable. Additional dining, business or conference facilities in the lounge may incur charges payable by you. Full terms of use apply and may be amended or terminated at any time. Read the Mastercard Flight Delay Pass Terms of Use.',
                description: 'Footnote seven legal copy covering Mastercard Flight Delay Pass eligibility and conditions on the Société Credit Card page',
              })}
            </BodyText>
          </LegalText>
        </ApplyTrustAndLegal>
      </LayoutContainer>
    </LoyaltyCreditCardCreamSection>
  )
}

export default LoyaltyCreditCardApplySection
